import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePagedData } from "@/hooks/usePagedData";
import { useModuleData } from "@/hooks/useModuleData";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { Pager, SearchInput } from "@/components/common/Pager";
import { eur, num } from "@/lib/format";
import api from "@/lib/api";
import { Package, Plus, PackageCheck, Tag, Globe } from "lucide-react";
import { toast } from "sonner";
import { openPdf } from "@/lib/pdf";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Materials() {
  const mats = usePagedData("/materials");
  const pos = usePagedData("/purchase-orders");
  const mmbe = useModuleData("/mm/stock-overview");
  const vendors = usePagedData("/vendors", 200);
  const { sites, activeSite } = useSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor_code: "", material_code: "", qty: "", unit_price: "", site_id: "" });
  const [committed, setCommitted] = useState({});

  useEffect(() => {
    const codes = (mats.items || []).map((m) => m.material_code);
    if (!codes.length) { setCommitted({}); return; }
    api.post("/materials/committed", { codes }).then((r) => setCommitted(r.data.committed || {})).catch(() => {});
  }, [mats.items]);

  const receive = async (po) => {
    await api.post(`/purchase-orders/${po.id}/goods-receipt`);
    toast.success(`Goods receipt posted for ${po.po_no}`);
    pos.reload();
  };

  const togglePublish = async (r) => {
    try {
      await api.post("/materials/webshop-bulk", { codes: [r.material_code], enable: !r.webshop_enabled, customers: ["28732"] });
      toast.success(r.webshop_enabled ? `${r.material_code} unpublished` : `${r.material_code} published to M&S portal`);
      mats.reload();
    } catch (e) { toast.error("Failed to update webshop status"); }
  };

  const createPo = async () => {
    const v = vendors.items?.find((x) => x.vendor_code === form.vendor_code);
    const m = mats.items?.find((x) => x.material_code === form.material_code);
    try {
      await api.post("/purchase-orders", {
        site_id: form.site_id || (activeSite !== "GROUP" ? activeSite : sites[0]?.id),
        vendor_code: form.vendor_code, vendor_name: v?.name || form.vendor_code,
        material_code: form.material_code, material_name: m?.name || form.material_code,
        qty: Number(form.qty), unit_price: Number(form.unit_price),
      });
      toast.success("Purchase order created");
      setOpen(false); setForm({ vendor_code: "", material_code: "", qty: "", unit_price: "", site_id: "" });
      pos.reload();
    } catch (e) { toast.error("Failed to create PO"); }
  };

  const matColumns = [
    { key: "img", label: "", render: (r) => r.image_path
      ? <img src={`${API}/api/shop/media/${r.id}?w=96`} alt="" data-testid={`mat-img-${r.material_code}`} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="h-10 w-10 rounded border border-[#e5e9f0] bg-white object-contain" />
      : <div data-testid={`mat-noimg-${r.material_code}`} className="flex h-10 w-10 items-center justify-center rounded border border-dashed border-[#e5e9f0] text-[#c8cfda]"><Package size={15} /></div> },
    { key: "material_code", label: "Material", render: (r) => <span className="font-mono text-xs text-[#F97316]">{r.material_code}</span> },
    { key: "name", label: "Description" },
    { key: "mat_group", label: "Group", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.mat_group}</span> },
    { key: "type", label: "Type", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.type}</span> },
    { key: "stock_qty", label: "On Hand", render: (r) => <span className="font-mono">{num(r.stock_qty)} {r.uom}</span> },
    { key: "unit_cost", label: "Unit Cost", render: (r) => <span className="font-mono">{eur(r.unit_cost)}</span> },
    { key: "abc_class", label: "ABC", render: (r) => <span className="font-mono">{r.abc_class}</span> },
    { key: "status", label: "Status", badge: true },
    { key: "reserved", label: "Reserved / Free", render: (r) => {
      const c = committed[r.material_code] || 0;
      return <span data-testid={`mat-reserved-${r.material_code}`} className="font-mono text-xs">
        <span className={c > 0 ? "text-amber-600 font-semibold" : "text-[#9aa0a6]"}>{num(c)}</span>
        <span className="text-[#c1c6cd]"> / </span><span className="text-[#32363a]">{num(r.free_stock)}</span></span>;
    } },
    { key: "webshop", label: "Webshop", render: (r) => (
      <button data-testid={`ws-toggle-${r.material_code}`} onClick={(e) => { e.stopPropagation(); togglePublish(r); }}
        className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ${r.webshop_enabled ? "bg-[#e6f4ea] text-[#137333] hover:bg-[#d6ecdc]" : "border border-[#c8cfda] text-[#6a6d70] hover:border-[#7c3aed]"}`}>
        <Globe size={12} /> {r.webshop_enabled ? "Live" : "Publish"}</button>) },
    { key: "actions", label: "", render: (r) => (
      <button data-testid={`mat-label-${r.material_code}`} onClick={(e) => { e.stopPropagation(); openPdf(`/print/label/material/${encodeURIComponent(r.material_code)}.pdf`); }} title="Print material label (4x6)"
        className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] px-2 py-1 text-[11px] hover:border-[#F97316]"><Tag size={13} /> Label</button>) },
  ];

  const poColumns = [
    { key: "po_no", label: "PO", render: (r) => <span className="font-mono text-xs text-[#F97316]">{r.po_no}</span> },
    { key: "vendor_name", label: "Vendor" },
    { key: "material_name", label: "Material", render: (r) => (
      <div><div className="text-[#32363a]">{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{num(r.qty)} {r.uom}</div></div>) },
    { key: "net_value", label: "Value", render: (r) => <span className="font-mono">{eur(r.net_value)}</span> },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (r.status !== "GR_COMPLETE" ? (
      <button data-testid={`gr-po-${r.po_no}`} onClick={() => receive(r)}
        className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] hover:border-[#10B981]">
        <PackageCheck size={13} /> Post GR (EP-MM10)</button>) : <span className="font-mono text-[10px] text-emerald-600">received</span>) },
  ];

  return (
    <div>
      <PageHeader title="Materials Management" subtitle="Real Euro Packaging material master (carrier bags, films, packaging) with stock, vendor purchase orders and goods receipt." icon={Package} accent="#F97316">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-po-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#F97316] px-4 py-2 text-sm font-semibold text-white hover:bg-[#ea580c]">
              <Plus size={16} /> New Purchase Order</button>
          </DialogTrigger>
          <DialogContent className="border-[#e5e9f0] bg-[#ffffff] text-[#32363a]">
            <DialogHeader><DialogTitle className="uppercase">Create Purchase Order</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <select data-testid="po-vendor-select" value={form.vendor_code} onChange={(e) => setForm({ ...form, vendor_code: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                <option value="">Select vendor…</option>{(vendors.items || []).map((v) => <option key={v.vendor_code} value={v.vendor_code}>{v.name}</option>)}
              </select>
              <select data-testid="po-material-select" value={form.material_code} onChange={(e) => setForm({ ...form, material_code: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                <option value="">Select material…</option>{(mats.items || []).map((m) => <option key={m.material_code} value={m.material_code}>{m.name}</option>)}
              </select>
              {activeSite === "GROUP" && (
                <select data-testid="po-site-select" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                  <option value="">Select site…</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>)}
              <input data-testid="po-qty-input" type="number" placeholder="Quantity" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
              <input data-testid="po-price-input" type="number" step="0.01" placeholder="Unit price (£)" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
            </div>
            <DialogFooter>
              <button data-testid="po-create-submit" onClick={createPo} disabled={!form.vendor_code || !form.material_code || !form.qty || !form.unit_price}
                className="rounded-lg bg-[#F97316] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Create PO</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Inventory Value" value={eur(mats.stats.stock_value || 0)} accent="#F97316" />
        <KpiCard label="Material SKUs" value={mats.total.toLocaleString()} accent="#F97316" />
        <KpiCard label="Low / Out of Stock" value={(mats.stats.low || 0).toLocaleString()} accent="#EF4444" />
        <KpiCard label="Procurement Spend" value={eur(pos.stats.net_value || 0)} accent="#0284C7" />
      </div>

      <Tabs defaultValue="stock">
        <TabsList className="bg-[#ffffff] border border-[#e5e9f0]">
          <TabsTrigger data-testid="tab-stock" value="stock" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#F97316]">Material Master (EP-MM01)</TabsTrigger>
          <TabsTrigger data-testid="tab-mmbe" value="mmbe" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#F97316]">Stock Overview (EP-MM02)</TabsTrigger>
          <TabsTrigger data-testid="tab-po" value="po" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#F97316]">Purchase Orders</TabsTrigger>
        </TabsList>
        <TabsContent value="stock" className="mt-4">
          <div className="mb-3"><SearchInput testId="materials-search" value={mats.q} onChange={mats.setQ} placeholder="Search materials by code, description, group…" /></div>
          {mats.loading && mats.items.length === 0 ? <Loader /> : <DataTable testId="materials-table" columns={matColumns} rows={mats.items} onRowClick={(r) => navigate(`/materials/${encodeURIComponent(r.material_code)}`)} />}
          <Pager testId="materials-pager" page={mats.page} pages={mats.pages} total={mats.total} pageSize={mats.pageSize} onPage={mats.setPage} />
        </TabsContent>
        <TabsContent value="mmbe" className="mt-4"><MmbeView data={mmbe.data} loading={mmbe.loading} /></TabsContent>
        <TabsContent value="po" className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <SearchInput testId="po-search" value={pos.q} onChange={pos.setQ} placeholder="Search POs by number, vendor, material…" />
            <button data-testid="new-po-me21n-btn" onClick={() => navigate("/purchase-orders/new")} className="inline-flex items-center gap-2 rounded-lg bg-[#F97316] px-4 py-2 text-sm font-semibold text-white hover:bg-[#ea580c]"><Plus size={16} /> New PO (EP-PU02)</button>
          </div>
          {pos.loading && pos.items.length === 0 ? <Loader /> : <DataTable testId="po-table" columns={poColumns} rows={pos.items} onRowClick={(r) => navigate(`/purchase-orders/${r.id}`)} />}
          <Pager testId="po-pager" page={pos.page} pages={pos.pages} total={pos.total} pageSize={pos.pageSize} onPage={pos.setPage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MmbeView({ data, loading }) {
  if (loading || !data) return <Loader />;
  const badge = { OK: "bg-emerald-100 text-emerald-700", LOW: "bg-amber-100 text-amber-700", OUT: "bg-red-100 text-red-700" };
  return (
    <div>
      <div className="mb-3 text-sm text-[#6a6d70]">{num(data.material_count)} materials · total valuation <span className="font-semibold text-[#F97316]">{eur(data.total_value)}</span> across all plants (EP-MM02)</div>
      <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white">
        <table className="w-full text-sm"><thead className="bg-[#f7f8fa] text-left text-[11px] uppercase tracking-wide text-[#6a6d70]"><tr><th className="px-4 py-2">Material</th><th>Type</th><th className="text-right">On Hand</th><th>By Plant</th><th className="px-4 text-right">Value</th></tr></thead>
          <tbody>{data.rows.map((r) => (<tr key={r.material_code} data-testid={`mmbe-${r.material_code}`} className="border-t border-[#f0f2f5] align-top">
            <td className="px-4 py-2"><div className="font-mono text-xs text-[#F97316]">{r.material_code}</div><div className="text-[#32363a]">{r.name}</div></td>
            <td className="text-[10px] text-[#6a6d70]">{r.type}</td>
            <td className="text-right font-mono">{num(r.total_qty)} {r.uom}</td>
            <td className="py-2"><div className="flex flex-wrap gap-1">{r.plants.map((p, i) => (<span key={i} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${badge[p.status] || "bg-gray-100 text-gray-600"}`}>{p.site_id} · {num(p.stock_qty)}</span>))}</div></td>
            <td className="px-4 text-right font-mono">{eur(r.total_value)}</td>
          </tr>))}</tbody></table>
      </div>
    </div>
  );
}
